import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Header from "@/components/Header";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { toast } from "sonner";
import { CheckCircle2, Lock } from "lucide-react";

export default function UpdatePage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [answers, setAnswers] = useState({ ag1: "", ag2: "", ag3: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (user === null || user === undefined) return; // check sessione in corso
    if (!user) {
      sessionStorage.setItem("pending_update_token", token);
      return;
    }
    api.post("/update-invites/resolve", { token })
      .then(({ data }) => setData(data))
      .catch(() => setError("Invito non valido o non più disponibile. Se hai bisogno di assistenza contatta l'istituto."));
  }, [user, token]);

  const submit = async () => {
    setBusy(true);
    try {
      await api.post("/update-invites/submit", { token, ...answers });
      setDone(true);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 404) setError(typeof detail === "string" ? detail : "Invito non più disponibile.");
      else toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const renderOptions = (qid, question, testid) => (
    <Card className="institutional-card" data-testid={testid}>
      <CardContent className="p-5">
        <p className="text-sm font-medium text-slate-900">{question.stem}</p>
        {question.original && (
          <div className="mt-3 border-l-2 border-slate-200 pl-3 text-xs text-slate-500" data-testid="update-ag3-original">
            <p className="italic">{question.original.stem}</p>
            <p className="mt-1 text-slate-700">La tua risposta del {data.submitted_date}: {question.original.option_text}</p>
          </div>
        )}
        {question.no_original && (
          <p className="mt-3 text-xs text-slate-500" data-testid="update-ag3-no-original">
            Alla prima compilazione non era stata definita una preferenza.
          </p>
        )}
        <RadioGroup className="mt-4 space-y-2" value={answers[qid]}
          onValueChange={(v) => setAnswers({ ...answers, [qid]: v })}>
          {question.options.map((o) => (
            <label key={o.id} data-testid={`${testid}-option-${o.id}`}
              className={`flex items-start gap-3 rounded-lg border p-3 cursor-pointer transition-colors ${
                answers[qid] === o.id ? "border-[#003366] bg-blue-50/60" : "border-slate-200 hover:border-slate-300"
              }`}>
              <RadioGroupItem value={o.id} className="mt-0.5" />
              <span className="text-sm text-slate-700">{o.label}</span>
            </label>
          ))}
        </RadioGroup>
      </CardContent>
    </Card>
  );

  let body;
  if (user === null || user === undefined) {
    body = <div className="h-8 w-8 animate-spin rounded-full border-4 border-slate-200 border-t-[#003366] mx-auto" />;
  } else if (!user) {
    body = (
      <Card className="institutional-card" data-testid="update-login-required">
        <CardContent className="p-8 text-center">
          <Lock className="h-10 w-10 text-[#003366] mx-auto mb-4" />
          <h1 className="font-display text-xl font-bold text-slate-900">Conferma della tua scelta ITS</h1>
          <p className="mt-3 text-sm text-slate-600 max-w-md mx-auto">
            Per confermare o aggiornare le informazioni della tua candidatura accedi con le credenziali
            usate in fase di registrazione.
          </p>
          <Button data-testid="update-login-button" className="mt-6 bg-[#003366] hover:bg-[#002244]" onClick={() => navigate("/auth")}>
            Vai all'accesso
          </Button>
        </CardContent>
      </Card>
    );
  } else if (error) {
    body = (
      <Card className="institutional-card" data-testid="update-error">
        <CardContent className="p-8 text-center">
          <h1 className="font-display text-xl font-bold text-slate-900">Invito non disponibile</h1>
          <p className="mt-3 text-sm text-slate-600 max-w-md mx-auto">{error}</p>
          <Button variant="outline" className="mt-6" onClick={() => navigate("/dashboard")}>Torna all'area riservata</Button>
        </CardContent>
      </Card>
    );
  } else if (!data) {
    body = <div className="h-8 w-8 animate-spin rounded-full border-4 border-slate-200 border-t-[#003366] mx-auto" data-testid="update-loading" />;
  } else if (done || data.completed) {
    body = (
      <Card className="institutional-card" data-testid="update-success">
        <CardContent className="p-8 text-center">
          <CheckCircle2 className="h-12 w-12 text-green-600 mx-auto mb-4" />
          <h1 className="font-display text-xl font-bold text-slate-900">Aggiornamento inviato</h1>
          <p className="mt-3 text-sm text-slate-600 max-w-md mx-auto">
            La commissione potrà utilizzarlo durante il colloquio.
          </p>
          <Button data-testid="update-back-dashboard" className="mt-6 bg-[#003366] hover:bg-[#002244]" onClick={() => navigate("/dashboard")}>
            Torna all'area riservata
          </Button>
        </CardContent>
      </Card>
    );
  } else {
    const complete = answers.ag1 && answers.ag2 && answers.ag3;
    body = (
      <div className="space-y-5" data-testid="update-form">
        <div>
          <p className="eyebrow mb-1">{data.tenant_name}</p>
          <h1 className="font-display text-2xl font-bold text-slate-900">Conferma della tua scelta ITS</h1>
          <p className="mt-2 text-sm text-slate-600">
            Compilazione di circa 2-3 minuti. Puoi uscire senza inviare e riprendere entro il {String(data.expires_at || "").slice(0, 10)}.
            Eventuali cambiamenti saranno approfonditi durante il colloquio e non modificano i punteggi dei test.
          </p>
        </div>
        {renderOptions("ag1", data.ag1, "update-ag1")}
        {renderOptions("ag2", data.ag2, "update-ag2")}
        {renderOptions("ag3", data.ag3, "update-ag3")}
        <Button data-testid="update-submit-button" size="lg" disabled={!complete || busy}
          className="w-full bg-[#003366] hover:bg-[#002244]" onClick={submit}>
          {busy ? "Invio in corso..." : "Invia aggiornamento"}
        </Button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <Header />
      <div className="max-w-2xl mx-auto px-4 py-12" data-testid="update-page">{body}</div>
    </div>
  );
}
